import { useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useHistoryStore } from '../store/historyStore.js'
import { saveGoalsData, saveGoalField } from '../data/goalsApi.js'
import { saveMetaData } from '../data/metaApi.js'

/**
 * useUndo — provides undo action + keyboard binding (⌘Z / Ctrl+Z).
 *
 * Revert strategy depends on what's in the snapshot:
 *
 *   - goalFieldRevert present (single-field edits):
 *       Apply via /goals/field — atomic, clobber-safe. Only the one
 *       field is reverted to its prior value; teammates' concurrent
 *       edits on other fields/goals are untouched.
 *
 *   - goalsBefore present (structural CRUD, legacy path):
 *       Full-state POST via /goals. Still has some clobber risk for
 *       concurrent edits made after the snapshot was captured. Kept
 *       for backward compat with structural ops; those are infrequent.
 *
 *   - metaBefore present:
 *       Full-meta POST. The worker's PRESERVED list shields weekly
 *       summary / team update / exec summary / action items /
 *       reactions / DMs from overwrite, so this is safe for most
 *       meta-level undo cases.
 *
 * Returns { undo, canUndo, lastLabel }.
 */
export function useUndo() {
  const queryClient = useQueryClient()
  const { history, popSnapshot } = useHistoryStore()
  const lastLabel = history[history.length - 1]?.label
  const canUndo = history.length > 0

  const undo = useCallback(async () => {
    const snapshot = popSnapshot()
    if (!snapshot) return

    const { goalsBefore, metaBefore, goalFieldRevert } = snapshot

    // Optimistic cache update for the goals side.
    if (goalFieldRevert) {
      // Apply the targeted revert to the cache so the UI flips back
      // instantly. The server roundtrip below confirms it.
      queryClient.setQueryData(['goals'], (old) => {
        if (!Array.isArray(old)) return old
        const next = structuredClone(old)
        const { pillarName, goalIndex, subgoalIndex, field, value } = goalFieldRevert
        const pi = next.findIndex((p) => p.pillar === pillarName)
        if (pi < 0) return old
        const goal = next[pi].goals?.[goalIndex]
        if (!goal) return old
        if (typeof subgoalIndex === 'number') {
          const sub = (goal.subgoals || goal.subGoals)?.[subgoalIndex]
          if (sub) sub[field] = value
        } else {
          goal[field] = value
        }
        return next
      })
    } else if (goalsBefore !== undefined) {
      queryClient.setQueryData(['goals'], goalsBefore)
    }

    if (metaBefore !== undefined) {
      queryClient.setQueryData(['meta'], metaBefore)
    }

    // Persist to server in parallel.
    const tasks = []
    if (goalFieldRevert) {
      tasks.push(saveGoalField(goalFieldRevert))
    } else if (goalsBefore !== undefined) {
      tasks.push(saveGoalsData(goalsBefore))
    }
    if (metaBefore !== undefined) tasks.push(saveMetaData(metaBefore))

    try {
      await Promise.all(tasks)
    } catch (err) {
      console.warn('[undo] Server save failed; cache still reverted', err)
    }
  }, [queryClient, popSnapshot])

  // Keyboard shortcut: ⌘Z on Mac, Ctrl+Z on Windows/Linux
  useEffect(() => {
    const onKey = (e) => {
      const isUndo =
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        (e.key === 'z' || e.key === 'Z')
      if (!isUndo) return
      // Don't hijack undo inside text inputs/textareas — let browser handle
      const target = e.target
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return
      }
      if (!canUndo) return
      e.preventDefault()
      undo()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canUndo, undo])

  return { undo, canUndo, lastLabel }
}
